import { Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Spinner, ToastHost } from '@os/ui';
import { useOsAuth } from '@os/auth-client/react';
import { useMe } from '../api';
import { appItems, systemItems, titleFor } from './items';
import { WindowsLogo } from './AppIcon';
import { Dock } from './Dock';
import { StartMenu } from './StartMenu';
import { Window } from './Window';
import './desktop.css';

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/**
 * The OS desktop: wallpaper, mac-style menu bar, Chrome OS-style launcher on the stage,
 * Windows-style dock + Start menu at the bottom. Non-launcher routes render inside a full-stage window with an
 * Explorer-style navigation pane. While that window is maximised the dock hides and a Start button moves into the menu
 * bar (Start menu then opens top-left); restoring the window brings the dock back underneath it.
 */
export function Desktop({ children }: { children: ReactNode }) {
  const { data } = useMe();
  const auth = useOsAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [startOpen, setStartOpen] = useState(false);
  /** Window maximised (full stage, dock hidden) or restored (framed, dock shown below). Kept across pages. */
  const [winMax, setWinMax] = useState(true);
  const now = useClock();

  const apps = useMemo(() => appItems(data), [data]);
  const system = useMemo(() => systemItems(data?.is_admin ?? false), [data?.is_admin]);
  const closeStart = useCallback(() => setStartOpen(false), []);
  useEffect(() => setStartOpen(false), [pathname]);

  const title = titleFor(pathname);
  const isLauncher = pathname === '/';
  const showDock = isLauncher || !winMax;
  const time = now.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  const date = now.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div className="dk" data-window={isLauncher ? '0' : '1'} data-dock={showDock ? '1' : '0'}>
      <header className="dk-menubar">
        {!showDock && (
          <button type="button" className="dk-menubar-start" aria-label="Start" aria-expanded={startOpen} aria-haspopup="dialog" data-open={startOpen ? '1' : '0'} onClick={() => setStartOpen((o) => !o)}>
            <WindowsLogo />
          </button>
        )}
        <Link to="/" className="dk-brand">OS</Link>
        <span className="dk-spacer" />
        {data && <button type="button" className="dk-meta dk-user" onClick={() => nav('/account')}>{data.user.display_name} · {data.organisation.name}</button>}
        <span className="dk-meta">{date}</span>
        <span className="dk-meta">{time}</span>
      </header>

      <main className="dk-stage">
        <Suspense fallback={<div className="dk-fallback"><Spinner /></div>}>
          {isLauncher ? children : (
            <Window title={title} max={winMax} onMaxChange={setWinMax} explorer={{ apps, system, userName: data?.user.display_name, orgName: data?.organisation.name, onSignOut: () => auth.logout() }}>{children}</Window>
          )}
        </Suspense>
      </main>

      {startOpen && (
        <StartMenu apps={apps} system={system} userName={data?.user.display_name} orgName={data?.organisation.name} placement={showDock ? 'bottom' : 'top'} onClose={closeStart} onSignOut={() => auth.logout()} />
      )}
      {showDock && (
        <Dock apps={apps} system={system} startOpen={startOpen} onToggleStart={() => setStartOpen((o) => !o)} userName={data?.user.display_name} onUser={() => nav('/account')} />
      )}
      <ToastHost />
    </div>
  );
}
